import * as THREE from 'three'
import type { PuppyDisplayNodeBodyBinding } from './types'
import { readPersistedDisplayNodeId } from './displaySnapshotObjects'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'

/**
 * 为可见显示网格分配稳定 displayNodeId。
 *
 * 设计说明：V6 连续保存要求删除一个组件不能让后续显示节点换号。重载网格优先复用包内
 * displayNodeId；完整 GLB 原始 Mesh 使用 assemblyNodeId 与 glTF node/primitive 来源定位；
 * 其它原生或新建网格继续使用 assemblyNodeId 与排序后的 geometryObjectId 组合身份；
 * 只有完全缺少业务绑定的边缘数据才保留序号兜底，并由后续完整性校验决定是否允许导出。
 *
 * @param input 当前显示网格及其稳定装配、BRep 绑定上下文。
 * @returns 本次导出使用的唯一 displayNodeId。
 */
export function allocateStableDisplayNodeId(input: {
  /** 当前导出事务已经占用的稳定 display node ID。 */
  readonly usedDisplayNodeIds: Set<string>
  /** 当前源显示网格。 */
  readonly source: THREE.Mesh
  /** 当前显示网格所属的稳定装配身份。 */
  readonly assemblyNodeId: string
  /** 当前显示网格的稳定 BRep/body 绑定。 */
  readonly bodyBindings: readonly PuppyDisplayNodeBodyBinding[]
  /** 缺少全部业务身份时使用的确定性兜底 ID。 */
  readonly fallbackDisplayNodeId: string
}): string {
  const persistedDisplayNodeId = readPersistedDisplayNodeId(input.source)
  const displaySource = ImportedModelObjectMetadataService.resolveDisplaySource(input.source)
  const geometryObjectIds = Array.from(new Set(
    input.bodyBindings.map((binding) => binding.geometryObjectId.trim()).filter(Boolean),
  )).sort()
  const completeGlbDisplayNodeId = displaySource?.kind === 'complete-glb'
    ? `display:${encodeURIComponent(input.assemblyNodeId)}:gltf-node:${displaySource.nodeIndex}:primitive:${displaySource.primitiveIndex}`
    : ''
  const geometryDisplayNodeId = geometryObjectIds.length > 0
    ? `display:${encodeURIComponent(input.assemblyNodeId)}:${geometryObjectIds.map(encodeURIComponent).join('+')}`
    : ''
  const stableDisplayNodeId = persistedDisplayNodeId
    || completeGlbDisplayNodeId
    || geometryDisplayNodeId

  if (stableDisplayNodeId) {
    if (!input.usedDisplayNodeIds.has(stableDisplayNodeId)) {
      input.usedDisplayNodeIds.add(stableDisplayNodeId)
      return stableDisplayNodeId
    }

    // 同一个来源包被插入多次时，两个实例会携带相同的持久化 displayNodeId。
    // display 身份必须在 occurrence 范围内唯一，因此仅对冲突实例追加稳定 assembly 作用域；
    // 生成后的作用域 ID 会随 V6 包写回，后续保存、删除其它实例时不会再次换号。
    if (persistedDisplayNodeId) {
      const occurrenceScopedDisplayNodeId = `display:${encodeURIComponent(input.assemblyNodeId)}:${encodeURIComponent(persistedDisplayNodeId)}`
      if (!input.usedDisplayNodeIds.has(occurrenceScopedDisplayNodeId)) {
        input.usedDisplayNodeIds.add(occurrenceScopedDisplayNodeId)
        return occurrenceScopedDisplayNodeId
      }
    }

    throw new Error(
      `PuppyCAD 可见模型存在重复稳定显示身份：displayNodeId=${stableDisplayNodeId}，assemblyNodeId=${input.assemblyNodeId}`,
    )
  }

  input.usedDisplayNodeIds.add(input.fallbackDisplayNodeId)
  return input.fallbackDisplayNodeId
}
